'use client';

import { useState, useCallback, useRef } from 'react';
import type { Locale } from '@/lib/content';
import type {
  ToolContent,
  UblValidationResult,
  UblValidationIssue,
  UblDocumentDetails,
  UblLineItem,
  UblTaxBreakdown,
  UblPartyAddress,
} from '@/lib/tools/types';
import { CheckCircle, Upload, RotateCcw, ArrowRight, XCircle, AlertTriangle, FileText } from 'lucide-react';
import Link from 'next/link';
import { ToolSignupWall } from './tool-signup-wall';
import { logUblValidation, createProcessingTimer } from '@/lib/tools/analytics';

interface UblValidatorProps {
  locale: Locale;
  content: ToolContent;
}

// Helper: get text content of first matching element within a parent
function getText(parent: Element | Document, localName: string): string | undefined {
  const el = parent.getElementsByTagNameNS('*', localName)[0];
  return el?.textContent?.trim() || undefined;
}

// Helper: get all elements matching a local name within a parent
function getAll(parent: Element | Document, localName: string): Element[] {
  return Array.from(parent.getElementsByTagNameNS('*', localName));
}

// Helper: parse a number from text, returns undefined if invalid
function parseNum(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const n = parseFloat(value);
  return isNaN(n) ? undefined : n;
}

// Helper: check decimal places
function getDecimalPlaces(value: string | undefined): number {
  if (!value) return 0;
  const parts = value.split('.');
  return parts.length > 1 ? parts[1].length : 0;
}

// Helper: add error with rule ID
function addError(
  errors: UblValidationIssue[],
  ruleId: string,
  field: string,
  message: string
): void {
  errors.push({ ruleId, field, message, severity: 'error' });
}

// Helper: add warning with rule ID
function addWarning(
  warnings: UblValidationIssue[],
  ruleId: string,
  field: string,
  message: string
): void {
  warnings.push({ ruleId, field, message, severity: 'warning' });
}

// Extract postal address from a party element
function extractAddress(partyEl: Element): UblPartyAddress {
  const addr: UblPartyAddress = {};
  const postalAddr = partyEl.getElementsByTagNameNS('*', 'PostalAddress')[0];
  if (postalAddr) {
    addr.street = getText(postalAddr, 'StreetName');
    addr.city = getText(postalAddr, 'CityName');
    addr.postalCode = getText(postalAddr, 'PostalZone');
    const countryEl = postalAddr.getElementsByTagNameNS('*', 'Country')[0];
    if (countryEl) {
      addr.country = getText(countryEl, 'IdentificationCode');
    }
  }
  return addr;
}

function parseAndValidateUbl(xmlString: string): UblValidationResult {
  const errors: UblValidationIssue[] = [];
  const warnings: UblValidationIssue[] = [];

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, 'text/xml');

    // Check for parse errors
    const parseError = doc.querySelector('parsererror');
    if (parseError) {
      return {
        valid: false,
        errors: [{ field: 'XML', message: 'Invalid XML format', severity: 'error' }],
        warnings: [],
      };
    }

    const root = doc.documentElement;
    const rootName = root.localName || root.nodeName;

    // Determine document type
    let documentType: string | undefined;
    if (rootName === 'Invoice') {
      documentType = 'Invoice';
    } else if (rootName === 'CreditNote') {
      documentType = 'CreditNote';
    } else {
      return {
        valid: false,
        errors: [{ field: 'Root', message: `Not a UBL Invoice or CreditNote document (found: ${rootName})`, severity: 'error' }],
        warnings: [],
      };
    }

    // Check namespace / UBL version
    const namespace = root.namespaceURI || '';
    let ublVersion: string | undefined;
    if (namespace.includes('2.1') || namespace.includes('UBL-2.1')) {
      ublVersion = '2.1';
    } else if (namespace.includes('2.0') || namespace.includes('UBL-2.0')) {
      ublVersion = '2.0';
      addWarning(warnings, 'UBL-VER', 'UBL Version', 'UBL 2.0 detected. Consider upgrading to UBL 2.1 for Peppol compliance');
    } else if (namespace.includes('urn:oasis:names:specification:ubl')) {
      ublVersion = '2.x';
    }

    // ============================================================
    // PEPPOL-EN16931-R008: No empty XML elements
    // ============================================================
    const allElements = root.getElementsByTagName('*');
    let emptyCount = 0;
    for (let i = 0; i < allElements.length; i++) {
      const el = allElements[i];
      if (el.childNodes.length === 0 && el.attributes.length === 0) {
        emptyCount++;
        if (emptyCount <= 3) {
          addWarning(warnings, 'PEPPOL-EN16931-R008', el.localName || el.nodeName,
            `Empty XML element "${el.localName || el.nodeName}" found. Empty elements should be removed`);
        }
      }
    }
    if (emptyCount > 3) {
      addWarning(warnings, 'PEPPOL-EN16931-R008', 'Multiple',
        `${emptyCount} empty XML elements found in total. Remove all empty elements`);
    }

    // ============================================================
    // BR-01: Specification identifier
    // ============================================================
    const customizationId = getText(root, 'CustomizationID');
    if (!customizationId) {
      addWarning(warnings, 'BR-01', 'cbc:CustomizationID',
        'Specification identifier (CustomizationID) is recommended for Peppol compliance');
    }

    // ============================================================
    // BR-02: Invoice number (ID)
    // ============================================================
    const invoiceId = getText(root, 'ID');
    if (!invoiceId) {
      addError(errors, 'BR-02', 'cbc:ID', 'Invoice/CreditNote number (ID) is mandatory');
    }

    // ============================================================
    // BR-03: Issue date
    // ============================================================
    const issueDate = getText(root, 'IssueDate');
    if (!issueDate) {
      addError(errors, 'BR-03', 'cbc:IssueDate', 'Issue date is mandatory');
    } else {
      const datePattern = /^\d{4}-\d{2}-\d{2}$/;
      if (!datePattern.test(issueDate)) {
        addError(errors, 'BR-03', 'cbc:IssueDate', 'Issue date must be in YYYY-MM-DD format');
      }
    }

    // ============================================================
    // BR-04: Invoice type code
    // ============================================================
    const invoiceTypeCode = getText(root, 'InvoiceTypeCode') || getText(root, 'CreditNoteTypeCode');
    if (!invoiceTypeCode) {
      addWarning(warnings, 'BR-04', 'cbc:InvoiceTypeCode',
        'Invoice type code is recommended (e.g., 380 for Invoice, 381 for CreditNote)');
    }

    // ============================================================
    // BR-05: Currency code
    // ============================================================
    const currencyCode = getText(root, 'DocumentCurrencyCode');
    if (!currencyCode) {
      addError(errors, 'BR-05', 'cbc:DocumentCurrencyCode', 'Document currency code is mandatory');
    }

    // Due date
    const dueDate = getText(root, 'DueDate') || getText(root, 'PaymentDueDate');

    // ============================================================
    // BR-06 / BR-08 / BR-09: Seller name, postal address, country
    // ============================================================
    const supplierParty = doc.getElementsByTagNameNS('*', 'AccountingSupplierParty')[0];
    if (!supplierParty) {
      addError(errors, 'BR-06', 'cac:AccountingSupplierParty', 'Seller (AccountingSupplierParty) is mandatory');
    }
    let supplierAddress: UblPartyAddress = {};
    if (supplierParty) {
      const party = supplierParty.getElementsByTagNameNS('*', 'Party')[0] || supplierParty;
      const sellerName = getText(party, 'Name');
      if (!sellerName) {
        addError(errors, 'BR-06', 'cac:AccountingSupplierParty/cac:Party/cbc:Name', 'Seller name is mandatory');
      }

      supplierAddress = extractAddress(party);
      const postalAddr = party.getElementsByTagNameNS('*', 'PostalAddress')[0];
      if (!postalAddr) {
        addError(errors, 'BR-08', 'cac:AccountingSupplierParty/cac:PostalAddress', 'Seller postal address is mandatory');
      } else if (!supplierAddress.country) {
        addError(errors, 'BR-09', 'cac:AccountingSupplierParty/cac:PostalAddress/cac:Country/cbc:IdentificationCode',
          'Seller country code is mandatory');
      }

      // BR-62: Seller electronic address scheme
      const sellerEndpoint = party.getElementsByTagNameNS('*', 'EndpointID')[0];
      if (sellerEndpoint) {
        const schemeId = sellerEndpoint.getAttribute('schemeID');
        if (!schemeId) {
          addWarning(warnings, 'BR-62', 'cac:AccountingSupplierParty/cbc:EndpointID/@schemeID',
            'Seller electronic address must have a scheme identifier (e.g., 0106 for NL KvK, 9944 for NL BTW)');
        }
      }
    }

    // ============================================================
    // BR-07 / BR-10 / BR-11: Buyer name, postal address, country
    // ============================================================
    const customerParty = doc.getElementsByTagNameNS('*', 'AccountingCustomerParty')[0];
    if (!customerParty) {
      addError(errors, 'BR-07', 'cac:AccountingCustomerParty', 'Buyer (AccountingCustomerParty) is mandatory');
    }
    let customerAddress: UblPartyAddress = {};
    if (customerParty) {
      const party = customerParty.getElementsByTagNameNS('*', 'Party')[0] || customerParty;
      const buyerName = getText(party, 'Name');
      if (!buyerName) {
        addError(errors, 'BR-07', 'cac:AccountingCustomerParty/cac:Party/cbc:Name', 'Buyer name is mandatory');
      }

      customerAddress = extractAddress(party);
      const postalAddr = party.getElementsByTagNameNS('*', 'PostalAddress')[0];
      if (!postalAddr) {
        addError(errors, 'BR-10', 'cac:AccountingCustomerParty/cac:PostalAddress', 'Buyer postal address is mandatory');
      } else if (!customerAddress.country) {
        addError(errors, 'BR-11', 'cac:AccountingCustomerParty/cac:PostalAddress/cac:Country/cbc:IdentificationCode',
          'Buyer country code is mandatory');
      }

      // BR-63: Buyer electronic address scheme
      const buyerEndpoint = party.getElementsByTagNameNS('*', 'EndpointID')[0];
      if (buyerEndpoint) {
        const schemeId = buyerEndpoint.getAttribute('schemeID');
        if (!schemeId) {
          addWarning(warnings, 'BR-63', 'cac:AccountingCustomerParty/cbc:EndpointID/@schemeID',
            'Buyer electronic address must have a scheme identifier');
        }
      }
    }

    // ============================================================
    // Monetary totals extraction and validation
    // ============================================================
    const legalMonetaryTotal = doc.getElementsByTagNameNS('*', 'LegalMonetaryTotal')[0];
    if (!legalMonetaryTotal) {
      addError(errors, 'BR-12', 'cac:LegalMonetaryTotal', 'LegalMonetaryTotal is mandatory');
    }

    const lineExtensionAmountStr = getText(legalMonetaryTotal || root, 'LineExtensionAmount');
    const taxExclusiveAmountStr = getText(legalMonetaryTotal || root, 'TaxExclusiveAmount');
    const taxInclusiveAmountStr = getText(legalMonetaryTotal || root, 'TaxInclusiveAmount');
    const payableAmountStr = getText(legalMonetaryTotal || root, 'PayableAmount');
    const allowanceTotalStr = getText(legalMonetaryTotal || root, 'AllowanceTotalAmount');
    const chargeTotalStr = getText(legalMonetaryTotal || root, 'ChargeTotalAmount');
    const prepaidStr = getText(legalMonetaryTotal || root, 'PrepaidAmount');

    const lineExtensionAmount = parseNum(lineExtensionAmountStr);
    const taxExclusiveAmount = parseNum(taxExclusiveAmountStr);
    const taxInclusiveAmount = parseNum(taxInclusiveAmountStr);
    const payableAmount = parseNum(payableAmountStr);
    const allowanceTotalAmount = parseNum(allowanceTotalStr) ?? 0;
    const chargeTotalAmount = parseNum(chargeTotalStr) ?? 0;
    const prepaidAmount = parseNum(prepaidStr) ?? 0;

    // BR-12: Line extension amount
    if (lineExtensionAmount === undefined) {
      addError(errors, 'BR-12', 'cbc:LineExtensionAmount', 'Sum of line net amounts (LineExtensionAmount) is mandatory');
    }

    // BR-13: Tax exclusive amount
    if (taxExclusiveAmount === undefined) {
      addError(errors, 'BR-13', 'cbc:TaxExclusiveAmount', 'Tax exclusive amount is mandatory');
    }

    // BR-14: Tax inclusive amount
    if (taxInclusiveAmount === undefined) {
      addError(errors, 'BR-14', 'cbc:TaxInclusiveAmount', 'Tax inclusive amount is mandatory');
    }

    // BR-15: Payable amount
    if (payableAmount === undefined) {
      addError(errors, 'BR-15', 'cbc:PayableAmount', 'Amount due for payment (PayableAmount) is mandatory');
    } else {
      // Check currencyID attribute
      const payableEl = (legalMonetaryTotal || root).getElementsByTagNameNS('*', 'PayableAmount')[0];
      if (payableEl && !payableEl.getAttribute('currencyID')) {
        addWarning(warnings, 'BR-CL-04', 'cbc:PayableAmount/@currencyID',
          'Currency ID attribute is recommended on PayableAmount');
      }
    }

    // ============================================================
    // BR-DEC: Decimal precision checks (max 2 decimal places for monetary amounts)
    // ============================================================
    const decimalChecks: [string, string, string | undefined][] = [
      ['BR-DEC-09', 'cbc:LineExtensionAmount', lineExtensionAmountStr],
      ['BR-DEC-12', 'cbc:TaxExclusiveAmount', taxExclusiveAmountStr],
      ['BR-DEC-14', 'cbc:TaxInclusiveAmount', taxInclusiveAmountStr],
      ['BR-DEC-18', 'cbc:PayableAmount', payableAmountStr],
      ['BR-DEC-10', 'cbc:AllowanceTotalAmount', allowanceTotalStr],
      ['BR-DEC-11', 'cbc:ChargeTotalAmount', chargeTotalStr],
      ['BR-DEC-16', 'cbc:PrepaidAmount', prepaidStr],
    ];
    for (const [ruleId, field, value] of decimalChecks) {
      if (value && getDecimalPlaces(value) > 2) {
        addWarning(warnings, ruleId, field,
          `${field} has more than 2 decimal places (${value}). Monetary amounts should have max 2 decimals`);
      }
    }

    // ============================================================
    // Tax totals extraction
    // ============================================================
    const taxTotalEl = doc.getElementsByTagNameNS('*', 'TaxTotal')[0];
    const totalTaxAmountStr = taxTotalEl ? getText(taxTotalEl, 'TaxAmount') : undefined;
    const totalTaxAmount = parseNum(totalTaxAmountStr);

    // BR-DEC-13: Tax amount decimal check
    if (totalTaxAmountStr && getDecimalPlaces(totalTaxAmountStr) > 2) {
      addWarning(warnings, 'BR-DEC-13', 'cbc:TaxAmount',
        `Total tax amount has more than 2 decimal places (${totalTaxAmountStr})`);
    }

    // ============================================================
    // Tax breakdown extraction (BR-45 to BR-48)
    // ============================================================
    const taxBreakdown: UblTaxBreakdown[] = [];
    const taxSubtotals = taxTotalEl ? getAll(taxTotalEl, 'TaxSubtotal') : [];

    // BR-CO-18: At least one VAT breakdown required
    if (taxSubtotals.length === 0) {
      addWarning(warnings, 'BR-CO-18', 'cac:TaxTotal/cac:TaxSubtotal',
        'At least one VAT breakdown (TaxSubtotal) is recommended');
    }

    for (let i = 0; i < taxSubtotals.length; i++) {
      const sub = taxSubtotals[i];
      const taxableStr = getText(sub, 'TaxableAmount');
      const taxAmtStr = getText(sub, 'TaxAmount');
      const taxCat = sub.getElementsByTagNameNS('*', 'TaxCategory')[0];
      const catCode = taxCat ? getText(taxCat, 'ID') : undefined;
      const rateStr = taxCat ? getText(taxCat, 'Percent') : undefined;
      const taxSchemeEl = taxCat?.getElementsByTagNameNS('*', 'TaxScheme')[0];
      const taxScheme = taxSchemeEl ? getText(taxSchemeEl, 'ID') : undefined;

      // BR-45: Taxable amount required
      if (!taxableStr) {
        addError(errors, 'BR-45', `cac:TaxSubtotal[${i + 1}]/cbc:TaxableAmount`,
          `Tax breakdown ${i + 1}: Taxable amount is mandatory`);
      }

      // BR-46: Tax amount required
      if (!taxAmtStr) {
        addError(errors, 'BR-46', `cac:TaxSubtotal[${i + 1}]/cbc:TaxAmount`,
          `Tax breakdown ${i + 1}: Tax amount is mandatory`);
      }

      // BR-47: Category code required
      if (!catCode) {
        addError(errors, 'BR-47', `cac:TaxSubtotal[${i + 1}]/cac:TaxCategory/cbc:ID`,
          `Tax breakdown ${i + 1}: VAT category code is mandatory`);
      }

      // BR-48: Rate required (unless exempt)
      if (!rateStr && catCode && !['O', 'E'].includes(catCode)) {
        addError(errors, 'BR-48', `cac:TaxSubtotal[${i + 1}]/cac:TaxCategory/cbc:Percent`,
          `Tax breakdown ${i + 1}: VAT rate is mandatory for category "${catCode}"`);
      }

      // BR-DEC-19/20: Decimal checks
      if (taxableStr && getDecimalPlaces(taxableStr) > 2) {
        addWarning(warnings, 'BR-DEC-19', `cac:TaxSubtotal[${i + 1}]/cbc:TaxableAmount`,
          `Tax breakdown ${i + 1}: Taxable amount has more than 2 decimal places`);
      }
      if (taxAmtStr && getDecimalPlaces(taxAmtStr) > 2) {
        addWarning(warnings, 'BR-DEC-20', `cac:TaxSubtotal[${i + 1}]/cbc:TaxAmount`,
          `Tax breakdown ${i + 1}: Tax amount has more than 2 decimal places`);
      }

      // BR-CO-17: Tax calculation check (tax = round(taxable * rate / 100, 2))
      const taxableVal = parseNum(taxableStr);
      const taxAmtVal = parseNum(taxAmtStr);
      const rateVal = parseNum(rateStr);
      if (taxableVal !== undefined && taxAmtVal !== undefined && rateVal !== undefined && rateVal > 0) {
        const expectedTax = Math.round(taxableVal * rateVal) / 100;
        const diff = Math.abs(taxAmtVal - expectedTax);
        if (diff > 0.02) {
          addError(errors, 'BR-CO-17', `cac:TaxSubtotal[${i + 1}]`,
            `Tax breakdown ${i + 1}: Tax amount (${taxAmtVal}) does not match calculated value (${expectedTax.toFixed(2)}) = taxable (${taxableVal}) x rate (${rateVal}%)`);
        }
      }

      taxBreakdown.push({
        taxableAmount: parseNum(taxableStr),
        taxAmount: parseNum(taxAmtStr),
        taxCategoryCode: catCode,
        taxRate: parseNum(rateStr),
        taxScheme,
      });
    }

    // ============================================================
    // Line items (BR-16, BR-21 to BR-27)
    // ============================================================
    const lineTag = documentType === 'Invoice' ? 'InvoiceLine' : 'CreditNoteLine';
    const lines = getAll(root, lineTag);

    // BR-16: At least one line required
    if (lines.length === 0) {
      addError(errors, 'BR-16', `cac:${lineTag}`, `At least one ${lineTag} is required`);
    }

    const lineItems: UblLineItem[] = [];
    let sumOfLineAmounts = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const lineNum = i + 1;

      // BR-21: Line ID
      const lineId = getText(line, 'ID');
      if (!lineId) {
        addError(errors, 'BR-21', `cac:${lineTag}[${lineNum}]/cbc:ID`,
          `Line ${lineNum}: Line identifier (ID) is mandatory`);
      }

      // BR-22: Invoiced/Credited quantity
      const qtyTag = documentType === 'Invoice' ? 'InvoicedQuantity' : 'CreditedQuantity';
      const qtyEl = line.getElementsByTagNameNS('*', qtyTag)[0];
      const qtyStr = qtyEl?.textContent?.trim();
      const quantity = parseNum(qtyStr);
      if (quantity === undefined) {
        addError(errors, 'BR-22', `cac:${lineTag}[${lineNum}]/cbc:${qtyTag}`,
          `Line ${lineNum}: Quantity is mandatory`);
      }

      // BR-23: Unit of measure
      const unitCode = qtyEl?.getAttribute('unitCode');
      if (qtyEl && !unitCode) {
        addWarning(warnings, 'BR-23', `cac:${lineTag}[${lineNum}]/cbc:${qtyTag}/@unitCode`,
          `Line ${lineNum}: Unit of measure code (unitCode) is recommended`);
      }

      // BR-24: Line net amount
      const lineAmountStr = getText(line, 'LineExtensionAmount');
      const lineAmount = parseNum(lineAmountStr);
      if (lineAmount === undefined) {
        addError(errors, 'BR-24', `cac:${lineTag}[${lineNum}]/cbc:LineExtensionAmount`,
          `Line ${lineNum}: Line net amount (LineExtensionAmount) is mandatory`);
      } else {
        sumOfLineAmounts += lineAmount;
        // BR-DEC-23: Line net amount decimal check
        if (lineAmountStr && getDecimalPlaces(lineAmountStr) > 2) {
          addWarning(warnings, 'BR-DEC-23', `cac:${lineTag}[${lineNum}]/cbc:LineExtensionAmount`,
            `Line ${lineNum}: Line net amount has more than 2 decimal places`);
        }
      }

      // BR-25: Item name
      const item = line.getElementsByTagNameNS('*', 'Item')[0];
      const itemName = item ? getText(item, 'Name') : undefined;
      const itemDesc = item ? getText(item, 'Description') : undefined;
      if (!itemName && !itemDesc) {
        addError(errors, 'BR-25', `cac:${lineTag}[${lineNum}]/cac:Item/cbc:Name`,
          `Line ${lineNum}: Item name or description is mandatory`);
      }

      // BR-26 / BR-27: Price
      const priceEl = line.getElementsByTagNameNS('*', 'Price')[0];
      const priceAmountStr = priceEl ? getText(priceEl, 'PriceAmount') : undefined;
      const priceAmount = parseNum(priceAmountStr);
      if (priceAmount === undefined) {
        addError(errors, 'BR-26', `cac:${lineTag}[${lineNum}]/cac:Price/cbc:PriceAmount`,
          `Line ${lineNum}: Item net price (PriceAmount) is mandatory`);
      } else if (priceAmount < 0) {
        addError(errors, 'BR-27', `cac:${lineTag}[${lineNum}]/cac:Price/cbc:PriceAmount`,
          `Line ${lineNum}: Item net price cannot be negative`);
      }

      // BR-28: Gross price cannot be negative
      const grossPriceStr = priceEl ? getText(priceEl, 'BaseAmount') : undefined;
      const grossPrice = parseNum(grossPriceStr);
      if (grossPrice !== undefined && grossPrice < 0) {
        addError(errors, 'BR-28', `cac:${lineTag}[${lineNum}]/cac:Price/cbc:BaseAmount`,
          `Line ${lineNum}: Item gross price (BaseAmount) cannot be negative`);
      }

      // Line VAT category (BR-CO-04)
      const lineTaxCat = line.getElementsByTagNameNS('*', 'TaxCategory')[0]
        || line.getElementsByTagNameNS('*', 'ClassifiedTaxCategory')[0];
      const lineVatCatCode = lineTaxCat ? getText(lineTaxCat, 'ID') : undefined;
      const lineVatRateStr = lineTaxCat ? getText(lineTaxCat, 'Percent') : undefined;
      if (!lineVatCatCode) {
        addWarning(warnings, 'BR-CO-04', `cac:${lineTag}[${lineNum}]/cac:TaxCategory/cbc:ID`,
          `Line ${lineNum}: VAT category code is recommended`);
      }

      // Line allowances (BR-41, BR-42)
      const lineAllowances = getAll(line, 'AllowanceCharge');
      for (let j = 0; j < lineAllowances.length; j++) {
        const ac = lineAllowances[j];
        const isCharge = getText(ac, 'ChargeIndicator') === 'true';
        const acAmount = getText(ac, 'Amount');
        const acReason = getText(ac, 'AllowanceChargeReason') || getText(ac, 'AllowanceChargeReasonCode');
        if (!acAmount) {
          addError(errors, isCharge ? 'BR-43' : 'BR-41',
            `cac:${lineTag}[${lineNum}]/cac:AllowanceCharge[${j + 1}]/cbc:Amount`,
            `Line ${lineNum}: ${isCharge ? 'Charge' : 'Allowance'} amount is mandatory`);
        }
        if (!acReason) {
          addWarning(warnings, isCharge ? 'BR-44' : 'BR-42',
            `cac:${lineTag}[${lineNum}]/cac:AllowanceCharge[${j + 1}]`,
            `Line ${lineNum}: ${isCharge ? 'Charge' : 'Allowance'} reason is recommended`);
        }
      }

      lineItems.push({
        lineId,
        description: itemDesc,
        quantity,
        unitCode: unitCode || undefined,
        unitPrice: priceAmount,
        lineAmount,
        vatRate: parseNum(lineVatRateStr),
        vatCategoryCode: lineVatCatCode,
        itemName,
      });
    }

    // ============================================================
    // Document-level allowances/charges (BR-31 to BR-38)
    // ============================================================
    const docAllowanceCharges = getAll(root, 'AllowanceCharge').filter(ac => {
      // Only document-level (not inside lines)
      let parent = ac.parentElement;
      while (parent) {
        if ((parent.localName || parent.nodeName) === lineTag) return false;
        parent = parent.parentElement;
      }
      return true;
    });

    for (let i = 0; i < docAllowanceCharges.length; i++) {
      const ac = docAllowanceCharges[i];
      const isCharge = getText(ac, 'ChargeIndicator') === 'true';
      const acAmount = getText(ac, 'Amount');
      const acReason = getText(ac, 'AllowanceChargeReason') || getText(ac, 'AllowanceChargeReasonCode');
      const acTaxCat = ac.getElementsByTagNameNS('*', 'TaxCategory')[0];

      if (!acAmount) {
        addError(errors, isCharge ? 'BR-36' : 'BR-31', `cac:AllowanceCharge[${i + 1}]/cbc:Amount`,
          `Document ${isCharge ? 'charge' : 'allowance'} ${i + 1}: Amount is mandatory`);
      }
      if (!acTaxCat) {
        addWarning(warnings, isCharge ? 'BR-37' : 'BR-32', `cac:AllowanceCharge[${i + 1}]/cac:TaxCategory`,
          `Document ${isCharge ? 'charge' : 'allowance'} ${i + 1}: VAT category is recommended`);
      }
      if (!acReason) {
        addWarning(warnings, isCharge ? 'BR-38' : 'BR-33', `cac:AllowanceCharge[${i + 1}]`,
          `Document ${isCharge ? 'charge' : 'allowance'} ${i + 1}: Reason is recommended`);
      }
    }

    // ============================================================
    // Calculation cross-checks (BR-CO-10, BR-CO-13, BR-CO-14, BR-CO-15, BR-CO-16)
    // ============================================================

    // BR-CO-10: Sum of line amounts = LineExtensionAmount
    if (lineExtensionAmount !== undefined && lines.length > 0) {
      const diff = Math.abs(sumOfLineAmounts - lineExtensionAmount);
      if (diff > 0.02) {
        addError(errors, 'BR-CO-10', 'cbc:LineExtensionAmount',
          `Sum of line net amounts (${sumOfLineAmounts.toFixed(2)}) does not match LineExtensionAmount (${lineExtensionAmount.toFixed(2)})`);
      }
    }

    // BR-CO-13: TaxExclusiveAmount = LineExtensionAmount - AllowanceTotalAmount + ChargeTotalAmount
    if (taxExclusiveAmount !== undefined && lineExtensionAmount !== undefined) {
      const expected = lineExtensionAmount - allowanceTotalAmount + chargeTotalAmount;
      const diff = Math.abs(taxExclusiveAmount - expected);
      if (diff > 0.02) {
        addError(errors, 'BR-CO-13', 'cbc:TaxExclusiveAmount',
          `Tax exclusive amount (${taxExclusiveAmount.toFixed(2)}) should equal LineExtensionAmount (${lineExtensionAmount.toFixed(2)}) - Allowances (${allowanceTotalAmount.toFixed(2)}) + Charges (${chargeTotalAmount.toFixed(2)}) = ${expected.toFixed(2)}`);
      }
    }

    // BR-CO-14: Total VAT = sum of TaxSubtotal amounts
    if (totalTaxAmount !== undefined && taxBreakdown.length > 0) {
      const sumSubtotalTax = taxBreakdown.reduce((sum, tb) => sum + (tb.taxAmount ?? 0), 0);
      const diff = Math.abs(totalTaxAmount - sumSubtotalTax);
      if (diff > 0.02) {
        addError(errors, 'BR-CO-14', 'cac:TaxTotal/cbc:TaxAmount',
          `Total VAT amount (${totalTaxAmount.toFixed(2)}) does not match sum of tax subtotals (${sumSubtotalTax.toFixed(2)})`);
      }
    }

    // BR-CO-15: TaxInclusiveAmount = TaxExclusiveAmount + TaxAmount
    if (taxInclusiveAmount !== undefined && taxExclusiveAmount !== undefined && totalTaxAmount !== undefined) {
      const expected = taxExclusiveAmount + totalTaxAmount;
      const diff = Math.abs(taxInclusiveAmount - expected);
      if (diff > 0.02) {
        addError(errors, 'BR-CO-15', 'cbc:TaxInclusiveAmount',
          `Tax inclusive amount (${taxInclusiveAmount.toFixed(2)}) should equal TaxExclusiveAmount (${taxExclusiveAmount.toFixed(2)}) + VAT (${totalTaxAmount.toFixed(2)}) = ${expected.toFixed(2)}`);
      }
    }

    // BR-CO-16: PayableAmount = TaxInclusiveAmount - PrepaidAmount + PayableRoundingAmount
    const roundingStr = getText(legalMonetaryTotal || root, 'PayableRoundingAmount');
    const roundingAmount = parseNum(roundingStr) ?? 0;
    if (payableAmount !== undefined && taxInclusiveAmount !== undefined) {
      const expected = taxInclusiveAmount - prepaidAmount + roundingAmount;
      const diff = Math.abs(payableAmount - expected);
      if (diff > 0.02) {
        addError(errors, 'BR-CO-16', 'cbc:PayableAmount',
          `Payable amount (${payableAmount.toFixed(2)}) should equal TaxInclusiveAmount (${taxInclusiveAmount.toFixed(2)}) - PrepaidAmount (${prepaidAmount.toFixed(2)}) + Rounding (${roundingAmount.toFixed(2)}) = ${expected.toFixed(2)}`);
      }
    }

    // ============================================================
    // BR-CO-26: Seller must have at least one identifier
    // ============================================================
    if (supplierParty) {
      const party = supplierParty.getElementsByTagNameNS('*', 'Party')[0] || supplierParty;
      const hasEndpoint = !!getText(party, 'EndpointID');
      const hasPartyId = getAll(party, 'PartyIdentification').length > 0;
      const hasCompanyId = !!getText(party, 'CompanyID');
      if (!hasEndpoint && !hasPartyId && !hasCompanyId) {
        addWarning(warnings, 'BR-CO-26', 'cac:AccountingSupplierParty',
          'Seller should have at least one identifier (EndpointID, PartyIdentification, or CompanyID)');
      }
    }

    // ============================================================
    // BR-CO-09: VAT IDs should have country prefix
    // ============================================================
    if (supplierParty) {
      const party = supplierParty.getElementsByTagNameNS('*', 'Party')[0] || supplierParty;
      const companyId = getText(party, 'CompanyID');
      if (companyId && companyId.length > 2 && !/^[A-Z]{2}/.test(companyId)) {
        addWarning(warnings, 'BR-CO-09', 'cac:AccountingSupplierParty/cac:PartyTaxScheme/cbc:CompanyID',
          `Seller VAT ID "${companyId}" should start with a country code prefix (e.g., NL, DE, BE)`);
      }
    }

    // ============================================================
    // BR-29: Invoice period end >= start
    // ============================================================
    const invoicePeriod = root.getElementsByTagNameNS('*', 'InvoicePeriod')[0];
    if (invoicePeriod) {
      const startDate = getText(invoicePeriod, 'StartDate');
      const endDate = getText(invoicePeriod, 'EndDate');
      if (startDate && endDate && endDate < startDate) {
        addError(errors, 'BR-29', 'cac:InvoicePeriod',
          `Invoice period end date (${endDate}) must be equal to or later than start date (${startDate})`);
      }
    }

    // ============================================================
    // BR-CO-25: Payment due date or terms
    // ============================================================
    if (payableAmount !== undefined && payableAmount > 0) {
      const hasDueDate = !!dueDate;
      const hasPaymentTerms = !!getText(root, 'Note') || getAll(root, 'PaymentTerms').length > 0;
      if (!hasDueDate && !hasPaymentTerms) {
        addWarning(warnings, 'BR-CO-25', 'cbc:DueDate',
          'Payment due date or payment terms should be provided when amount is due');
      }
    }

    // ============================================================
    // PEPPOL-EN16931-R001: Process ID (ProfileID)
    // ============================================================
    const profileId = getText(root, 'ProfileID');
    if (!profileId) {
      addWarning(warnings, 'PEPPOL-EN16931-R001', 'cbc:ProfileID',
        'Business process identifier (ProfileID) is required for Peppol compliance');
    }

    // ============================================================
    // NL-R specific: Check for NL sellers
    // ============================================================
    if (supplierAddress.country === 'NL') {
      // NL sellers should have KvK or BTW number
      if (supplierParty) {
        const party = supplierParty.getElementsByTagNameNS('*', 'Party')[0] || supplierParty;
        const companyId = getText(party, 'CompanyID');
        const hasLegalEntity = getAll(party, 'PartyLegalEntity').length > 0;
        if (!companyId && !hasLegalEntity) {
          addWarning(warnings, 'NL-R-001', 'cac:AccountingSupplierParty',
            'Dutch sellers should provide a KvK number (PartyLegalEntity/CompanyID) or BTW number (PartyTaxScheme/CompanyID)');
        }
      }
    }

    // ============================================================
    // Extract document details for logging
    // ============================================================
    const documentDetails: UblDocumentDetails = {
      invoiceId: invoiceId,
      issueDate: issueDate,
      dueDate: dueDate,
      invoiceTypeCode: invoiceTypeCode,
      currency: currencyCode || (payableAmount !== undefined ?
        (legalMonetaryTotal || root).getElementsByTagNameNS('*', 'PayableAmount')[0]?.getAttribute('currencyID') || undefined
        : undefined),
      totalAmount: payableAmountStr,
      lineCount: lines.length,
      lineExtensionAmount,
      taxExclusiveAmount,
      taxInclusiveAmount,
      taxAmount: totalTaxAmount,
      allowanceTotalAmount: allowanceTotalAmount || undefined,
      chargeTotalAmount: chargeTotalAmount || undefined,
      prepaidAmount: prepaidAmount || undefined,
      supplierAddress,
      customerAddress,
      lineItems: lineItems.length > 0 ? lineItems : undefined,
      taxBreakdown: taxBreakdown.length > 0 ? taxBreakdown : undefined,
    };

    // Supplier details
    if (supplierParty) {
      const party = supplierParty.getElementsByTagNameNS('*', 'Party')[0] || supplierParty;
      documentDetails.supplierName = getText(party, 'Name');
      documentDetails.supplierCountry = supplierAddress.country;
      const taxSchemes = getAll(party, 'PartyTaxScheme');
      for (const ts of taxSchemes) {
        const cid = getText(ts, 'CompanyID');
        if (cid) { documentDetails.supplierVat = cid; break; }
      }
      documentDetails.supplierRegistrationName = getText(party, 'RegistrationName');
      documentDetails.supplierEndpointId = getText(party, 'EndpointID');
    }

    // Customer details
    if (customerParty) {
      const party = customerParty.getElementsByTagNameNS('*', 'Party')[0] || customerParty;
      documentDetails.customerName = getText(party, 'Name');
      documentDetails.customerCountry = customerAddress.country;
      const taxSchemes = getAll(party, 'PartyTaxScheme');
      for (const ts of taxSchemes) {
        const cid = getText(ts, 'CompanyID');
        if (cid) { documentDetails.customerVat = cid; break; }
      }
      documentDetails.customerRegistrationName = getText(party, 'RegistrationName');
      documentDetails.customerEndpointId = getText(party, 'EndpointID');
    }

    return {
      valid: errors.length === 0,
      ublVersion,
      documentType,
      documentDetails,
      errors,
      warnings,
    };
  } catch (e) {
    return {
      valid: false,
      errors: [{ field: 'XML', message: `Parse error: ${(e as Error).message}`, severity: 'error' }],
      warnings: [],
    };
  }
}

function buildLogPayload(
  locale: Locale,
  validationResult: UblValidationResult,
  processingTimeMs: number,
  fileName?: string,
  fileSize?: number,
) {
  const d = validationResult.documentDetails;
  return {
    locale,
    file_name: fileName,
    file_size: fileSize,
    is_valid: validationResult.valid,
    error_count: validationResult.errors.length,
    warning_count: validationResult.warnings.length,
    errors: validationResult.errors.map(e => ({ ruleId: e.ruleId, field: e.field, message: e.message })),
    warnings: validationResult.warnings.map(w => ({ ruleId: w.ruleId, field: w.field, message: w.message })),
    ubl_version: validationResult.ublVersion,
    invoice_type: validationResult.documentType,
    invoice_type_code: d?.invoiceTypeCode,
    processing_time_ms: processingTimeMs,
    invoice_id: d?.invoiceId,
    issue_date: d?.issueDate,
    due_date: d?.dueDate,
    supplier_name: d?.supplierName,
    customer_name: d?.customerName,
    currency: d?.currency,
    total_amount: d?.totalAmount,
    line_count: d?.lineCount,
    supplier_country: d?.supplierCountry,
    customer_country: d?.customerCountry,
    supplier_vat: d?.supplierVat,
    customer_vat: d?.customerVat,
    supplier_registration_name: d?.supplierRegistrationName,
    customer_registration_name: d?.customerRegistrationName,
    supplier_endpoint_id: d?.supplierEndpointId,
    customer_endpoint_id: d?.customerEndpointId,
    tax_exclusive_amount: d?.taxExclusiveAmount?.toFixed(2),
    tax_inclusive_amount: d?.taxInclusiveAmount?.toFixed(2),
    tax_amount: d?.taxAmount?.toFixed(2),
    line_extension_amount: d?.lineExtensionAmount?.toFixed(2),
    supplier_address_json: d?.supplierAddress ? JSON.stringify(d.supplierAddress) : undefined,
    customer_address_json: d?.customerAddress ? JSON.stringify(d.customerAddress) : undefined,
    line_items_json: d?.lineItems ? JSON.stringify(d.lineItems) : undefined,
    tax_breakdown_json: d?.taxBreakdown ? JSON.stringify(d.taxBreakdown) : undefined,
  };
}

export function UblValidator({ locale, content }: UblValidatorProps) {
  const [xmlContent, setXmlContent] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [result, setResult] = useState<UblValidationResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const labels = content.labels;
  const errs = content.errors ?? {};

  const handleValidate = useCallback(() => {
    if (!xmlContent.trim()) {
      setResult({
        valid: false,
        errors: [{ field: 'Input', message: errs.fileRequired ?? 'Upload a file or paste XML code', severity: 'error' }],
        warnings: [],
      });
      return;
    }

    const timer = createProcessingTimer();
    const validationResult = parseAndValidateUbl(xmlContent);
    setResult(validationResult);
    logUblValidation(buildLogPayload(locale, validationResult, timer.getElapsed()));
  }, [xmlContent, errs.fileRequired, locale]);

  const handleFileUpload = (file: File) => {
    if (!file.name.endsWith('.xml')) {
      setResult({
        valid: false,
        errors: [{ field: 'File', message: errs.invalidXml ?? 'Invalid XML file', severity: 'error' }],
        warnings: [],
      });
      return;
    }

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setXmlContent(text);

      const timer = createProcessingTimer();
      const validationResult = parseAndValidateUbl(text);
      setResult(validationResult);
      logUblValidation(buildLogPayload(locale, validationResult, timer.getElapsed(), file.name, file.size));
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const reset = () => {
    setXmlContent('');
    setFileName('');
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-100">
          <CheckCircle className="h-7 w-7 text-purple-700" />
        </div>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          {content.title}
        </h2>
        <p className="mt-3 text-lg text-slate-600">
          {content.description}
        </p>
      </div>

      {/* Validator Card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        {/* File Upload */}
        <div
          className={`relative mb-4 rounded-xl border-2 border-dashed p-8 text-center transition-all ${
            isDragging ? 'border-brand-500 bg-brand-50' : 'border-slate-300 hover:border-slate-400'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xml"
            onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
            className="absolute inset-0 cursor-pointer opacity-0"
          />
          <Upload className="mx-auto h-10 w-10 text-slate-400" />
          <p className="mt-3 text-sm font-medium text-slate-700">{labels.uploadFile}</p>
          {fileName && (
            <p className="mt-2 flex items-center justify-center gap-2 text-sm text-brand-600">
              <FileText className="h-4 w-4" />
              {fileName}
            </p>
          )}
        </div>

        {/* Or Paste XML */}
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            {labels.orPasteXml}
          </label>
          <textarea
            value={xmlContent}
            onChange={(e) => {
              setXmlContent(e.target.value);
              setFileName('');
            }}
            placeholder={labels.pasteXml}
            rows={8}
            className="block w-full rounded-xl border border-slate-300 p-4 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>

        {/* Validate Button */}
        <button
          type="button"
          onClick={handleValidate}
          className="w-full rounded-xl bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-brand-700"
        >
          {labels.validate}
        </button>

        {/* Result */}
        {result && (
          <div className={`mt-6 rounded-xl p-4 ${result.valid ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className="flex items-center gap-3">
              {result.valid ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : (
                <XCircle className="h-6 w-6 text-red-600" />
              )}
              <span className={`font-semibold ${result.valid ? 'text-green-800' : 'text-red-800'}`}>
                {result.valid ? labels.valid : labels.invalid}
              </span>
            </div>

            {/* Document Info */}
            {(result.ublVersion || result.documentType) && (
              <div className="mt-4 flex flex-wrap gap-2">
                {result.ublVersion && (
                  <span className="rounded-lg bg-white px-3 py-1 text-sm font-medium text-slate-700">
                    {labels.ublVersion}: {result.ublVersion}
                  </span>
                )}
                {result.documentType && (
                  <span className="rounded-lg bg-white px-3 py-1 text-sm font-medium text-slate-700">
                    {labels.documentType}: {result.documentType}
                  </span>
                )}
                {result.documentDetails?.invoiceTypeCode && (
                  <span className="rounded-lg bg-white px-3 py-1 text-sm font-medium text-slate-700">
                    Type Code: {result.documentDetails.invoiceTypeCode}
                  </span>
                )}
              </div>
            )}

            {/* Parsed Document Details */}
            {result.documentDetails && Object.values(result.documentDetails).some(v => v !== undefined) && (
              <div className="mt-4 rounded-lg bg-white p-4">
                <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <FileText className="h-4 w-4" />
                  {labels.documentDetails || 'Document Details'}
                </h4>
                <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                  {result.documentDetails.invoiceId && (
                    <div>
                      <span className="text-slate-500">{labels.invoiceId || 'ID'}:</span>{' '}
                      <span className="font-medium text-slate-800">{result.documentDetails.invoiceId}</span>
                    </div>
                  )}
                  {result.documentDetails.issueDate && (
                    <div>
                      <span className="text-slate-500">{labels.issueDate || 'Date'}:</span>{' '}
                      <span className="font-medium text-slate-800">{result.documentDetails.issueDate}</span>
                    </div>
                  )}
                  {result.documentDetails.dueDate && (
                    <div>
                      <span className="text-slate-500">{labels.dueDate || 'Due Date'}:</span>{' '}
                      <span className="font-medium text-slate-800">{result.documentDetails.dueDate}</span>
                    </div>
                  )}
                  {result.documentDetails.supplierName && (
                    <div>
                      <span className="text-slate-500">{labels.supplier || 'Supplier'}:</span>{' '}
                      <span className="font-medium text-slate-800">{result.documentDetails.supplierName}</span>
                      {result.documentDetails.supplierCountry && (
                        <span className="ml-1 text-slate-400">({result.documentDetails.supplierCountry})</span>
                      )}
                    </div>
                  )}
                  {result.documentDetails.supplierVat && (
                    <div>
                      <span className="text-slate-500">{labels.supplierVat || 'Supplier VAT'}:</span>{' '}
                      <span className="font-medium text-slate-800">{result.documentDetails.supplierVat}</span>
                    </div>
                  )}
                  {result.documentDetails.customerName && (
                    <div>
                      <span className="text-slate-500">{labels.customer || 'Customer'}:</span>{' '}
                      <span className="font-medium text-slate-800">{result.documentDetails.customerName}</span>
                      {result.documentDetails.customerCountry && (
                        <span className="ml-1 text-slate-400">({result.documentDetails.customerCountry})</span>
                      )}
                    </div>
                  )}
                  {result.documentDetails.customerVat && (
                    <div>
                      <span className="text-slate-500">{labels.customerVat || 'Customer VAT'}:</span>{' '}
                      <span className="font-medium text-slate-800">{result.documentDetails.customerVat}</span>
                    </div>
                  )}
                  {result.documentDetails.totalAmount && (
                    <div>
                      <span className="text-slate-500">{labels.totalAmount || 'Total'}:</span>{' '}
                      <span className="font-medium text-slate-800">
                        {result.documentDetails.currency && `${result.documentDetails.currency} `}
                        {result.documentDetails.totalAmount}
                      </span>
                    </div>
                  )}
                  {result.documentDetails.taxExclusiveAmount !== undefined && (
                    <div>
                      <span className="text-slate-500">{labels.taxExclusive || 'Tax Exclusive'}:</span>{' '}
                      <span className="font-medium text-slate-800">
                        {result.documentDetails.currency && `${result.documentDetails.currency} `}
                        {result.documentDetails.taxExclusiveAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {result.documentDetails.taxAmount !== undefined && (
                    <div>
                      <span className="text-slate-500">{labels.vatAmount || 'VAT'}:</span>{' '}
                      <span className="font-medium text-slate-800">
                        {result.documentDetails.currency && `${result.documentDetails.currency} `}
                        {result.documentDetails.taxAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {result.documentDetails.lineCount !== undefined && result.documentDetails.lineCount > 0 && (
                    <div>
                      <span className="text-slate-500">{labels.lineCount || 'Lines'}:</span>{' '}
                      <span className="font-medium text-slate-800">{result.documentDetails.lineCount}</span>
                    </div>
                  )}
                </div>

                {/* Line Items Table */}
                {result.documentDetails.lineItems && result.documentDetails.lineItems.length > 0 && (
                  <div className="mt-4 border-t border-slate-100 pt-4">
                    <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {labels.lineItems || 'Line Items'}
                    </h5>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-slate-200 text-left text-slate-500">
                            <th className="pb-1 pr-2">#</th>
                            <th className="pb-1 pr-2">{labels.itemDescription || 'Item'}</th>
                            <th className="pb-1 pr-2 text-right">{labels.qty || 'Qty'}</th>
                            <th className="pb-1 pr-2 text-right">{labels.price || 'Price'}</th>
                            <th className="pb-1 pr-2 text-right">{labels.vatPercent || 'VAT %'}</th>
                            <th className="pb-1 text-right">{labels.amount || 'Amount'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.documentDetails.lineItems.map((item, idx) => (
                            <tr key={idx} className="border-b border-slate-50">
                              <td className="py-1 pr-2 text-slate-400">{item.lineId || idx + 1}</td>
                              <td className="py-1 pr-2 font-medium text-slate-700">{item.itemName || item.description || '-'}</td>
                              <td className="py-1 pr-2 text-right text-slate-600">
                                {item.quantity !== undefined ? item.quantity : '-'}
                                {item.unitCode && <span className="ml-0.5 text-slate-400">{item.unitCode}</span>}
                              </td>
                              <td className="py-1 pr-2 text-right text-slate-600">{item.unitPrice !== undefined ? item.unitPrice.toFixed(2) : '-'}</td>
                              <td className="py-1 pr-2 text-right text-slate-600">{item.vatRate !== undefined ? `${item.vatRate}%` : item.vatCategoryCode || '-'}</td>
                              <td className="py-1 text-right font-medium text-slate-800">{item.lineAmount !== undefined ? item.lineAmount.toFixed(2) : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Tax Breakdown */}
                {result.documentDetails.taxBreakdown && result.documentDetails.taxBreakdown.length > 0 && (
                  <div className="mt-4 border-t border-slate-100 pt-4">
                    <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {labels.taxBreakdown || 'VAT Breakdown'}
                    </h5>
                    <div className="space-y-1">
                      {result.documentDetails.taxBreakdown.map((tb, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="text-slate-600">
                            {tb.taxCategoryCode || 'VAT'} {tb.taxRate !== undefined ? `${tb.taxRate}%` : ''}
                          </span>
                          <span className="text-slate-600">
                            {labels.taxable || 'Taxable'}: {tb.taxableAmount?.toFixed(2) ?? '-'}
                          </span>
                          <span className="font-medium text-slate-800">
                            {labels.tax || 'Tax'}: {tb.taxAmount?.toFixed(2) ?? '-'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Errors */}
            {result.errors.length > 0 && (
              <div className="mt-4">
                <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-red-800">
                  <XCircle className="h-4 w-4" />
                  {labels.errors} ({result.errors.length})
                </h4>
                <ul className="space-y-2">
                  {result.errors.map((error, index) => (
                    <li key={index} className="rounded-lg bg-white p-3 text-sm">
                      <div className="flex items-center gap-2">
                        {error.ruleId && (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-xs font-medium text-red-700">{error.ruleId}</span>
                        )}
                        <span className="font-mono text-xs text-slate-500">{error.field}</span>
                      </div>
                      <p className="mt-1 text-red-700">{error.message}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Warnings */}
            {result.warnings.length > 0 && (
              <div className="mt-4">
                <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-800">
                  <AlertTriangle className="h-4 w-4" />
                  {labels.warnings} ({result.warnings.length})
                </h4>
                <ul className="space-y-2">
                  {result.warnings.map((warning, index) => (
                    <li key={index} className="rounded-lg bg-white p-3 text-sm">
                      <div className="flex items-center gap-2">
                        {warning.ruleId && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-xs font-medium text-amber-700">{warning.ruleId}</span>
                        )}
                        <span className="font-mono text-xs text-slate-500">{warning.field}</span>
                      </div>
                      <p className="mt-1 text-amber-700">{warning.message}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* No issues */}
            {result.valid && result.errors.length === 0 && result.warnings.length === 0 && (
              <p className="mt-4 text-sm text-green-700">{labels.noErrors}</p>
            )}
          </div>
        )}

        {/* Clear Button */}
        <button
          type="button"
          onClick={reset}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RotateCcw className="h-4 w-4" />
          {labels.clear}
        </button>
      </div>

      {/* Privacy Notice */}
      <div className="mt-6 rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-600">
        {content.privacyNotice}
      </div>

      {/* Signup Wall */}
      <ToolSignupWall locale={locale} />

      {/* CTA Banner */}
      <div className="mt-8 rounded-2xl bg-gradient-deep p-6 text-white">
        <h2 className="text-xl font-semibold">{content.ctaTitle}</h2>
        <p className="mt-2 text-brand-100">{content.ctaDescription}</p>
        <Link
          href={`/${locale}/signup/`}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-brand-700 transition-all hover:bg-brand-50"
        >
          {content.ctaButton}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Instructions */}
      {content.instructions && content.instructions.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">
            {locale === 'nl' ? 'Hoe werkt het?' : locale === 'de' ? 'Wie funktioniert es?' : locale === 'fr' ? 'Comment ça marche?' : 'How does it work?'}
          </h3>
          <ol className="space-y-2">
            {content.instructions.map((instruction, index) => (
              <li key={index} className="flex gap-3 text-slate-700">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-medium text-brand-700">
                  {index + 1}
                </span>
                {instruction}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Disclaimer */}
      {content.disclaimer && (
        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            {content.disclaimer.heading}
          </h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            {content.disclaimer.content}
          </p>
        </div>
      )}
    </div>
  );
}
